import { Component, OnDestroy, OnInit } from '@angular/core';
import { DirectoryService } from './services/directory-service.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Branch } from '../shared/enums/branch.enum';
import { Subject, forkJoin, interval, combineLatest } from 'rxjs';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { takeUntil, take } from 'rxjs/operators';

@Component({
    selector: 'app-room-directory',
    templateUrl: './room-directory.component.html',
    styleUrls: ['./room-directory.component.scss'],
    standalone: false
})
export class RoomDirectoryComponent implements OnInit, OnDestroy {
  public filteredMembers: any[];
  public filterSelection: string;
  categories: string[] = ['All Members', 'Executive Branch', 'Senators', 'Representatives', 'Joint Committees', 'Senate Committees', 'House Committees', 'Other'];
  hearingRooms: string[] = ['A1', 'A2', 'B1', 'B2', 'Gardner Auditorium', 'House Chamber', 'House Gallery', '428', 'Senate Chamber', 'Senate Gallery']; //available hearing rooms to navigate to from the DirectionSettings.json
  private dataMap: any = {};
  public showSpinner: boolean = true;
  faCircleNotch = faCircleNotch;
  private destroy$: Subject<void> = new Subject<void>();

  constructor(protected http: HttpClient,
    protected router: Router,
    protected service: DirectoryService,
    protected toastr: ToastrService) { }

  ngOnInit(): void {
    this.filterSelection = 'All Members';
    this.loadData();
    interval(900000) // Refresh data every 15 minutes
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadData();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData(): void {
    combineLatest([
      this.service.getAllExecs().pipe(take(1)),
      this.service.getMembers(Branch.senate).pipe(take(1)),
      this.service.getMembers(Branch.house).pipe(take(1)),
      this.service.getAllJointCommittees().pipe(take(1)),
      this.service.getAllSenateCommittees().pipe(take(1)),
      this.service.getAllHouseCommittees().pipe(take(1)),
      this.service.getAllOthers().pipe(take(1))
    ]).subscribe(([execs, senators, reps, jointCommittees, senateCommittees, houseCommittees, other]) => {
      this.dataMap = {
        'All Members':  senators.concat(reps),
        'Executive Branch': execs,
        'Senators': senators,
        'Representatives': reps,
        'Joint Committees': jointCommittees,
        'Senate Committees': senateCommittees,
        'House Committees': houseCommittees,
        'Other': other
      };
      this.filteredMembers = this.dataMap['All Members'];
      this.showSpinner = false;
    });
  }

  handleToggle(selection: string): void { 
    this.filterSelection = selection;
    this.filteredMembers = this.dataMap[this.filterSelection];
    this.showSpinner = false;
  }

  getDirection(member: any, room: string, code: string) {
    if (this.filterSelection === 'Executive Branch' ) {
      return this.toastr.warning('Direction is not yet available for this specific location'); //TBD MassLeg api WayfinderMothership need to be updated to add more directions
    }

    else if (this.filterSelection === 'Other') {
      const matchingRoom = this.hearingRooms.find(hearingRoom => {
        const cleanedRoom = hearingRoom.toLowerCase();
        if (cleanedRoom.includes(room.toLowerCase())) {
          return true;
        }
        if (room.toLowerCase().includes(cleanedRoom)) {
          return true;
        }
        return false;
      });

      if (matchingRoom) {
        const matchedRoom = this.mapRoomToTarget(matchingRoom);
        this.router.navigate(['directions/', matchedRoom.trim(), '']);
      } else {
        this.toastr.warning('Direction is not yet available for this specific location'); // TBD MassLeg api WayfinderMothership need to be updated to add more directions
      }
    }

    else if (this.isNotCommittee(member.name, member.reportingName)) {
      this.router.navigate(['directions/', room.trim(), code.trim()]);
    } else {
      this.router.navigate(['directions/', room.trim(), '']);
    }
  }

  isNotCommittee(name: string, reportingName: string): boolean {
    return (name && !name.includes('Committee')) || (reportingName && !reportingName.includes('Committee'));
  }

  mapRoomToTarget(roomName: string): string | undefined {
    const roomMappings: { [roomName: string]: string } = {
      'A1': 'A1',
      'A-1': 'A1',
      'A2': 'A2',
      'A-2': 'A2',
      'B1': 'B1',
      'B-1': 'B1',
      'B2': 'B2',
      'B-2': 'B2',
      'Gardner Auditorium': '4635',
      'Gardner': '4635',
      'House Chamber': '3282',
      'House Gallery': '3385',
      'Room 428': '428',
      'Senate Chamber': '544',
      'Senate Gallery': '3399'
    };
    return roomMappings[roomName];
  }
}
